import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectBooleanInput,
  NumberInput,
  FormErrors
} from 'blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock, CardBreakout } from '../../card';
import Callout from '../../callout';
import { useTerms } from '../../../../contexts/terms';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';

interface EpxGenericData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  times: string[];
  mode: string;
  days: number[];
  step?: number;
  isESRD: boolean;
  isBinary?: boolean;
  activityAlertThreshold?: number | null;
}

type EpxGenericDataRaw = DateSerialized<EpxGenericData, 'start' | 'end'> & {
  _activityAlertEnabled?: boolean;
};

export const EpxGenericDefaultData: EpxGenericData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  times: ['10:00'],
  mode: 'W',
  days: [3, 5],
  step: 1,
  isESRD: false,
  isBinary: false,
  activityAlertThreshold: null
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxGenericFormProps {
  setForm?: (form: FormInstance<EpxGenericDataRaw>) => void;
  onSubmit?: (data: EpxGenericData) => void;
  isDisabled?: boolean;
}

const EpxGenericForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxGenericFormProps): JSX.Element => {
  const { terms } = useTerms();
  const serialize = (data: EpxGenericDataRaw): EpxGenericData => {
    const { _activityAlertEnabled, ...rest } = data;
    const serialized = {
      ...rest,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, 1),
      activityAlertThreshold: _activityAlertEnabled
        ? (data.activityAlertThreshold ?? 4)
        : null
    };
    return serialized;
  };
  const deserialize = (data: EpxGenericData): EpxGenericDataRaw => {
    return {
      ...data,
      _activityAlertEnabled: data.activityAlertThreshold != null,
      activityAlertThreshold: data.activityAlertThreshold ?? 4,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxGenericDefaultData);
  const form = new FormInstance<EpxGenericDataRaw, EpxGenericData>(
    defaultData,
    { serialize, deserialize }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxGenericData): void => {
    _onSubmit(data);
  };
  const validate = (values: EpxGenericDataRaw): FormErrors => {
    const errors: FormErrors = {};
    if (values._activityAlertEnabled) {
      const threshold = values.activityAlertThreshold;
      if (
        threshold == null ||
        isNaN(threshold) ||
        !Number.isInteger(threshold) ||
        threshold < 1 ||
        threshold > 5
      ) {
        errors.activityAlertThreshold =
          'Please enter a valid whole number between 1 and 5.';
      }
    }
    return errors;
  };
  return (
    <CardBlock bare>
      <Form
        form={form}
        onSubmit={onSubmit}
        disabled={isDisabled}
        validate={validate}
      >
        {(props) => (
          <>
            <CardBlock>
              <Row>
                <Col sm={12}>
                  <SelectMethod />
                </Col>
                <Col sm={12}>
                  <SelectLanguage />
                </Col>
              </Row>
            </CardBlock>
            <CardBlock>
              <Row>
                <Col sm={12}>
                  <StartDate label="Start Date" name="start" fill />
                </Col>
                <Col sm={12}>
                  <EndDate label="End Date" name="end" />
                </Col>
                <Col sm={12}>
                  <SelectTime label="Message At" name="times[0]" />
                </Col>
                <Col sm={12}>
                  <Timezone label="Timezone" name="tz" fill />
                </Col>
              </Row>
            </CardBlock>
            <CardBreakout>
              <Weekday name="days" minItems={1} maxItems={2} inline />
            </CardBreakout>
            <CardBlock>
              <Row>
                <Col sm={12}>
                  <SelectBooleanInput
                    label="Use ESRD Questions"
                    name="isESRD"
                    fill
                  />
                  <Callout>
                    Saying 'Yes' to this means the {terms.patient.default} will
                    receive questions relating to End-Stage Renal Disease
                    (ESRD).
                  </Callout>
                </Col>
                <Col sm={12}>
                  <SelectBooleanInput
                    label="Binary Mode"
                    name="isBinary"
                    fill
                  />
                </Col>
                <Col sm={12} style={{ marginTop: '1rem' }}>
                  <SelectBooleanInput
                    label="Enable Activity Alert Threshold"
                    name="_activityAlertEnabled"
                    fill
                  />
                  <Callout>
                    When enabled, if a {terms.patient.default} reports an
                    activity level at or above the threshold, a follow-up alert
                    will always be triggered regardless of previous responses.
                  </Callout>
                </Col>
                {props.values._activityAlertEnabled && (
                  <Col sm={12} style={{ marginTop: '1rem' }}>
                    <NumberInput
                      label="Activity Alert Threshold (1-5)"
                      name="activityAlertThreshold"
                      min={1}
                      max={5}
                      fill
                    />
                  </Col>
                )}
              </Row>
            </CardBlock>
          </>
        )}
      </Form>
    </CardBlock>
  );
};

export default EpxGenericForm;
